/**
 * Firebase Client SDK Configuration
 *
 * Enhanced with comprehensive error handling for Firestore connections,
 * offline persistence, and WebSocket failures.
 *
 * Network Error Handling (Jan 2026):
 * - Single-tab persistence with forceOwnership to prevent deadlocks
 * - Automatic long-polling fallback for WebSocket failures
 * - Graceful handling of network suspension (laptop sleep/wake)
 * - Transient error suppression for better UX
 * - Debounced network state changes for flaky connections
 * - Proper cleanup for SPA hot module replacement
 *
 * @see https://firebase.google.com/docs/firestore/manage-data/enable-offline
 * @see https://github.com/firebase/firebase-js-sdk/issues/1436
 */

import { initializeApp } from 'firebase/app';
import { initializeAuth, browserLocalPersistence, connectAuthEmulator } from 'firebase/auth';
import {
  connectFirestoreEmulator,
  initializeFirestore,
  memoryLocalCache,
  enableNetwork,
  disableNetwork
} from 'firebase/firestore';
import type { Functions } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

// Extend Window interface for type safety
declare global {
  interface Window {
    __firebaseFunctions?: Functions;
    __firebaseCleanup?: () => void;
  }
}

// Firebase client configuration is public by design. Keep production fallbacks so
// local/manual hosting builds do not ship an unusable bundle when Vite env vars
// are absent.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'AIzaSyCl7JqZlp4cx4uyG43z5LDE20LIrPWC6WI',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'corbel-ai-pipeline.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'corbel-ai-pipeline',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'corbel-ai-pipeline.firebasestorage.app',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '808194535374',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:808194535374:web:23c16b0f2210e5361b059f',
};

// Initialize Firebase
export const app = initializeApp(firebaseConfig);

/**
 * Initialize Firebase Auth with localStorage persistence (atomic)
 *
 * CRITICAL FIX (Feb 2026): Uses initializeAuth() instead of getAuth() + setPersistence()
 * to set persistence atomically at initialization — no race condition between
 * setPersistence() and getRedirectResult(). The popup/redirect resolver is supplied
 * explicitly at the Google auth call sites so Safari and mobile browsers do not
 * initialize its cross-origin helper iframe on unrelated public pages.
 *
 * Previous approach (getAuth + fire-and-forget setPersistence) caused Google Sign-In
 * redirect loop: setPersistence raced with getRedirectResult, wiping auth state.
 */
export const auth = initializeAuth(app, {
  persistence: browserLocalPersistence,
});

/**
 * Initialize Firestore with memory-only cache
 *
 * TEMPORARY FIX (Jan 2026):
 * Using memory cache instead of IndexedDB persistence to fix the infinite loading
 * issue caused by corrupted IndexedDB state from persistentMultipleTabManager.
 *
 * This means:
 * - Data is NOT cached between page refreshes
 * - Each page load fetches fresh data from server
 * - Works reliably without IndexedDB deadlocks
 *
 * TODO: Re-enable persistence after user clears browser data, or implement
 * proper IndexedDB corruption detection and automatic cleanup.
 *
 * Features:
 * - Long-polling fallback for WebSocket failures (fixes 400 channel errors)
 * - Automatic reconnection on network recovery
 */
export const db = initializeFirestore(app, {
  localCache: memoryLocalCache(),
  experimentalForceLongPolling: false, // Enable if WebSocket issues persist
  experimentalAutoDetectLongPolling: true, // Auto-switch on WebSocket failure
});

/**
 * Firestore connection state tracking
 */
export interface FirestoreConnectionState {
  isOnline: boolean;
  hasPersistence: boolean;
  persistenceType: 'indexeddb' | 'memory' | 'none';
  lastError?: string;
  lastNetworkChange?: Date;
}

const connectionState: FirestoreConnectionState = {
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  hasPersistence: false, // Using memory cache - no persistence
  persistenceType: 'memory',
};

/**
 * Cleanup functions for proper SPA disposal
 * Prevents memory leaks during hot module replacement
 */
const cleanupFunctions: (() => void)[] = [];

/**
 * Cleanup Firebase listeners and timers
 * Call this when the module is being disposed (HMR, page unload)
 */
export function cleanupFirebase(): void {
  cleanupFunctions.forEach(fn => {
    try {
      fn();
    } catch (error) {
      console.debug('[Firebase] Cleanup error (non-critical):', error);
    }
  });
  cleanupFunctions.length = 0;
}

/**
 * Handle network state changes with Firestore reconnection
 *
 * This addresses the "Failed to obtain primary lease" errors that occur
 * when the network recovers after suspension (laptop sleep, WiFi change, etc.)
 *
 * Improvements (10x Review Dec 2025):
 * - Debounced to handle network flapping (2s stabilization)
 * - Prevents concurrent reconnection attempts
 * - Proper cleanup for SPA hot module replacement
 * - Scoped error suppression (only Firestore errors)
 *
 * @see https://github.com/firebase/firebase-js-sdk/issues/1436
 */
if (typeof window !== 'undefined') {
  // Cleanup any existing listeners from previous module loads (HMR)
  if (window.__firebaseCleanup) {
    window.__firebaseCleanup();
  }

  // State tracking for debounced reconnection
  let networkDebounceTimeout: ReturnType<typeof setTimeout> | null = null;
  let isReconnecting = false;
  const NETWORK_DEBOUNCE_MS = 2000; // Wait 2s for network to stabilize

  /**
   * Debounced network state change handler
   * Prevents rapid reconnection attempts on flaky networks
   */
  const handleNetworkChange = async (isOnline: boolean): Promise<void> => {
    // Clear any pending debounce
    if (networkDebounceTimeout) {
      clearTimeout(networkDebounceTimeout);
      networkDebounceTimeout = null;
    }

    // Debounce network state changes to prevent rapid reconnections
    networkDebounceTimeout = setTimeout(async () => {
      networkDebounceTimeout = null;
      connectionState.lastNetworkChange = new Date();

      if (isOnline) {
        // Prevent concurrent reconnection attempts
        if (isReconnecting) {
          console.debug('[Firebase] Reconnection already in progress, skipping');
          return;
        }

        console.log('[Firebase] Network connection restored');
        connectionState.isOnline = true;
        isReconnecting = true;

        try {
          await enableNetwork(db);
          console.log('[Firebase] Firestore network re-enabled successfully');
        } catch (error) {
          console.debug('[Firebase] Network recovery handled by SDK:', error);
        } finally {
          isReconnecting = false;
        }
      } else {
        console.warn('[Firebase] Network connection lost - using cached data');
        connectionState.isOnline = false;

        try {
          await disableNetwork(db);
          console.log('[Firebase] Firestore network disabled for offline mode');
        } catch (error) {
          console.debug('[Firebase] Offline transition handled by SDK:', error);
        }
      }
    }, NETWORK_DEBOUNCE_MS);
  };

  // Named handlers for proper cleanup
  const onlineHandler = (): void => {
    handleNetworkChange(true);
  };

  const offlineHandler = (): void => {
    handleNetworkChange(false);
  };

  const visibilityHandler = (): void => {
    if (document.visibilityState === 'visible' && navigator.onLine) {
      // Page became visible and we're online - ensure connection is healthy
      // Use requestIdleCallback for non-blocking check (falls back to setTimeout)
      const callback = (): void => {
        if (navigator.onLine && !connectionState.isOnline && !isReconnecting) {
          console.debug('[Firebase] Page visible - reconnecting');
          handleNetworkChange(true);
        }
      };

      if ('requestIdleCallback' in window) {
        requestIdleCallback(callback, { timeout: 1000 });
      } else {
        setTimeout(callback, 500);
      }
    }
  };

  // Add event listeners
  window.addEventListener('online', onlineHandler);
  window.addEventListener('offline', offlineHandler);
  document.addEventListener('visibilitychange', visibilityHandler);

  // Register cleanup functions
  cleanupFunctions.push(() => {
    window.removeEventListener('online', onlineHandler);
    window.removeEventListener('offline', offlineHandler);
    document.removeEventListener('visibilitychange', visibilityHandler);

    if (networkDebounceTimeout) {
      clearTimeout(networkDebounceTimeout);
      networkDebounceTimeout = null;
    }
  });

  /**
   * Scoped error suppression for Firestore transient errors ONLY
   *
   * IMPORTANT: Only suppresses errors that:
   * 1. Contain known Firestore transient error patterns
   * 2. Originate from Firestore/WebChannel stack (stack trace check)
   *
   * Non-Firestore errors pass through unchanged.
   */
  const originalConsoleError = console.error;
  const suppressedPatterns = [
    'Failed to obtain primary lease',
    'WebChannelConnection RPC',
    'ERR_NETWORK_IO_SUSPENDED',
    'ERR_NETWORK_CHANGED',
    'channel transport errored',
    'FIRESTORE (10.', // SDK version errors
    'FIRESTORE (11.',
    'FIRESTORE (12.',
  ];

  console.error = (...args: unknown[]) => {
    const message = args[0]?.toString() || '';
    const errorArg = args[0];

    // Check if this is a Firestore-related error
    let isFirestoreError = false;

    // Check stack trace if available
    if (errorArg instanceof Error && errorArg.stack) {
      isFirestoreError =
        errorArg.stack.includes('firestore') ||
        errorArg.stack.includes('WebChannel') ||
        errorArg.stack.includes('@firebase');
    }

    // Also check message content for Firestore patterns
    const containsFirestorePattern = suppressedPatterns.some(pattern =>
      message.includes(pattern)
    );

    // Only suppress if it's a known transient Firestore error. Firestore
    // internal assertions are SDK bugs/edge cases and must remain visible.
    if ((isFirestoreError || containsFirestorePattern) && !message.includes('INTERNAL ASSERTION FAILED')) {
      // Verify it's actually a transient error, not a real Firestore bug
      const isTransientError = suppressedPatterns.some(err =>
        message.includes(err)
      );

      if (isTransientError) {
        console.debug(
          '[Firebase] Transient network event (handled automatically):',
          message.substring(0, 100)
        );
        return;
      }
    }

    // Pass through all non-Firestore and non-transient errors
    originalConsoleError.apply(console, args);
  };

  // Register console.error restore in cleanup
  cleanupFunctions.push(() => {
    console.error = originalConsoleError;
  });

  // Store cleanup function globally for HMR
  window.__firebaseCleanup = cleanupFirebase;
}

/**
 * Get current Firestore connection state
 *
 * Useful for debugging connection issues or displaying network status to users
 */
export function getConnectionState(): Readonly<FirestoreConnectionState> {
  return { ...connectionState };
}

export const storage = getStorage(app);
export const functions = getFunctions(app, 'us-central1');

// Debug logging for Firebase initialization
if (typeof window !== 'undefined') {
  console.log('🔥 Firebase SDK initialized:', {
    projectId: app.options.projectId,
    authDomain: app.options.authDomain,
    functionsRegion: 'us-central1',
    hasFunctions: !!functions,
    functionsApp: functions.app.name,
  });

  // Make functions globally available for debugging (typed)
  window.__firebaseFunctions = functions;
  console.log('🔍 Debug: Firebase functions available at window.__firebaseFunctions');
}

// Connect to emulators in development
if (import.meta.env.DEV) {
  const useEmulators = import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true';

  if (useEmulators) {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, '127.0.0.1', 8080);
    connectStorageEmulator(storage, '127.0.0.1', 9199);
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
    console.log('🔧 Connected to Firebase Emulators');
  }
}
